import React, { useState, MouseEvent, useEffect, useRef } from 'react';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Unstable_Grid2';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { ShippingPortalParcel } from '@lanterds/types';
import { deleteFavorite, getFavorites } from 'src/api/shippingPortal';
import { Box } from '@mui/material';
import './Favorites.scss';
import FmtpTable from 'src/components/common/FmtpTable/FmtpTable';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import { useSnackbar, SnackbarKey } from 'notistack';

import { DealerContainer } from './DealerContainer';
import { ShippingPortalFavorite } from 'src/util/types';
import { CellContext, ColumnDef } from '@tanstack/react-table';

export default function Favorites({
    printLabels,
    onTabChange,
    initialShowFavoritesValue = true,
    generatedParcels,
    isPrinting,
}: {
    printLabels: (parcelsToGenerate: ShippingPortalParcel[]) => void;
    onTabChange: (event: MouseEvent<HTMLLIElement | HTMLButtonElement>, createNew: boolean) => void;
    initialShowFavoritesValue?: boolean;
    generatedParcels?: ShippingPortalParcel[];
    isPrinting: boolean;
}) {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const [selectedRows, setSelectedRows] = useState<{ [rowId: string]: boolean }>({});
    const [showFavorites, setShowFavorites] = useState(initialShowFavoritesValue);
    const [selectedFavorite, setSelectedFavorite] = useState<ShippingPortalFavorite | null>(null);
    const [favoritesList, setFavoritesList] = useState<ShippingPortalFavorite[]>([]);
    const opSnackbarRef = useRef<SnackbarKey | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [favoriteToDelete, setFavoriteToDelete] = useState<string | null>(null);

    const { data: favoritesData, isFetching: isFavoritesFetching, refetch: refetchFavorites } = getFavorites();
    const { mutateAsync: deleteFavoriteAsync, isPending: isDeletePending } = deleteFavorite();

    useEffect(() => {
        if (selectedFavorite) {
            const tempFavoritesList: ShippingPortalFavorite[] = [];
            for (const favorite of favoritesList) {
                if (favorite.id === selectedFavorite.id) {
                    // Skip any favorites that no longer have parcels
                    if (selectedFavorite.parcelsToPrint.length) {
                        tempFavoritesList.push(selectedFavorite);
                    }
                } else {
                    tempFavoritesList.push(favorite);
                }
            }
            setFavoritesList(tempFavoritesList);
        }
    }, [selectedFavorite]);

    useEffect(() => {
        if (favoritesData) {
            setFavoritesList(favoritesData);
        }
    }, [favoritesData, isFavoritesFetching]);

    useEffect(() => {
        if (showFavorites) {
            setSelectedFavorite(null);
        }
    }, [showFavorites]);

    useEffect(() => {
        let message: string | null = null;

        if (isDeletePending) {
            message = 'Deleting favorite…';
        } else if (isFavoritesFetching) {
            message = 'Loading favorites…';
        }

        if (message) {
            if (opSnackbarRef.current) closeSnackbar(opSnackbarRef.current);

            opSnackbarRef.current = enqueueSnackbar(message, {
                variant: 'loading',
                preventDuplicate: true,
                disableWindowBlurListener: true,
                autoHideDuration: null,
                action: (key) => (
                    <Button size="small" onClick={() => closeSnackbar(key)}>
                        Dismiss
                    </Button>
                ),
            });
        } else if (opSnackbarRef.current) {
            closeSnackbar(opSnackbarRef.current);
            opSnackbarRef.current = null;
        }
    }, [isFavoritesFetching, isDeletePending, enqueueSnackbar, closeSnackbar]);

    const handleOnDelete = async (id: string) => {
        setFavoriteToDelete(id);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!favoriteToDelete) return;
        setDeleteDialogOpen(false);
        try {
            await deleteFavoriteAsync(favoriteToDelete);
            setFavoritesList((list) => list.filter((fav) => fav.id !== favoriteToDelete));
            enqueueSnackbar('Favorite deleted.', { variant: 'success' });
        } catch (err) {
            console.error(err);
            enqueueSnackbar('Failed to delete favorite.', { variant: 'error' });
        } finally {
            setFavoriteToDelete(null);
        }
    };

    const handleOnFavoriteClick = (index: number) => {
        setSelectedFavorite(favoritesList[index]);
        setShowFavorites(false);
    };

    const updateCurrentParcels = (index: number, updatedParcel: ShippingPortalParcel) => {
        if (selectedFavorite) {
            const tempParcels = [...selectedFavorite.parcelsToPrint];

            tempParcels[index] = updatedParcel;

            setSelectedFavorite({ ...selectedFavorite, parcelsToPrint: tempParcels });
        }
    };

    const applyToAllParcels = (name: string, value: string | number) => {
        if (selectedFavorite) {
            setSelectedFavorite({
                ...selectedFavorite,
                parcelsToPrint: selectedFavorite.parcelsToPrint.map((parcel) => {
                    return { ...parcel, [name]: value };
                }),
            });
        }
    };

    const columns: ColumnDef<any>[] = [
        {
            accessorKey: 'name',
            header: 'Name',
            meta: {
                innerHtml: ({ row, getValue }: CellContext<any, any>) => {
                    const { index } = row;
                    return (
                        <Box className="">
                            <Button
                                variant="contained"
                                type="submit"
                                className="favorite-name-button"
                                onClick={() => handleOnFavoriteClick(index)}
                            >
                                {getValue()}
                            </Button>
                        </Box>
                    );
                },
            },
        },
        {
            accessorKey: 'dealerCount',
            header: 'Count of Dealers',
        },
        {
            accessorKey: 'labelQuantity',
            header: 'Quantity of Labels',
        },
        {
            accessorKey: 'quickPrint',
            header: 'Quick Print',
            enableColumnFilter: false,
            enableResizing: false,
            meta: {
                innerHtml: ({ row }: CellContext<any, unknown>) => {
                    const { index } = row;
                    return (
                        <Box className="">
                            <Button
                                variant="contained"
                                type="submit"
                                className="quick-print-button"
                                onClick={() => printLabels(favoritesList[index].parcelsToPrint)}
                            >
                                Print
                            </Button>
                        </Box>
                    );
                },
            },
        },
        {
            id: 'remove',
            size: 20,
            enableColumnFilter: false,
            enableResizing: false,
            meta: {
                innerHtml: ({ row }: CellContext<any, unknown>) => {
                    const { id } = row.original;
                    return (
                        <Box className="delete-icon-container">
                            <DeleteTwoToneIcon color="primary" onClick={() => handleOnDelete(id)} />
                        </Box>
                    );
                },
            },
        },
    ];

    return (
        <>
            {showFavorites ? (
                <Card id="favorites" className="auto-fill-height">
                    <CardContent className="table-card auto-fill-height">
                        <FmtpTable
                            isFetching={isFavoritesFetching || isDeletePending}
                            loadingIndicatorMessage={isFavoritesFetching ? 'Getting Favorites...' : 'Deleting Favorite...'}
                            data={favoritesList}
                            columns={columns}
                            setSelectedRows={setSelectedRows}
                            selectedRows={selectedRows}
                            enableSorting={true}
                        />
                        <Grid className="buttons">
                            <CardActions>
                                <Button
                                    variant="contained"
                                    type="submit"
                                    className="submit-button"
                                    onClick={(event) => onTabChange(event, true)}
                                >
                                    Create New
                                </Button>
                            </CardActions>
                        </Grid>
                    </CardContent>
                </Card>
            ) : (
                <DealerContainer
                    printLabels={printLabels}
                    showFavoritesDetails={!showFavorites}
                    setShowFavorites={setShowFavorites}
                    selectedFavorite={selectedFavorite}
                    setSelectedFavorite={setSelectedFavorite}
                    parcels={generatedParcels}
                    updateCurrentParcels={updateCurrentParcels}
                    applyToAllParcels={applyToAllParcels}
                    isPrinting={isPrinting}
                    fillHeight={true}
                    refetchFavorites={refetchFavorites}
                />
            )}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete Favorite</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete the selected favorite? This cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="primary" onClick={handleConfirmDelete}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
